import React, { useEffect, useState } from 'react';
import { Drawer, TextField, Tooltip, Select, MenuItem } from '@mui/material';
import { Button, Divider, TextInput, Toggle } from 'pepsico-ds';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GoalsModal from './GoalsModal';
import {
  cycleDatesInputOptions,
  cycleToggleOptions,
  goalMetricOptions
} from '../../utils/Constants';
import { convertStringToCamelCase } from '../../utils/Utils';
import { triggerNotification } from '../../utils/NotificationContext';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import './HomePage.css';

const CycleDrawer = (props) => {
  const {
    showDrawer,
    handleClose,
    selectedBu,
    handleCreateCycle,
    screenName = 'home',
    selectedCycleObj = {},
    handleRunCycle,
    isSteeringRecoCompleted = false,
    periodsData = []
  } = props;

  const initialState = {
    cycleName: '',
    testCycle: 'No',
    activeCycle: 'No',
    startDate: null,
    endDate: null,
    threshold: null
  };
  const [cycleDetailsObj, setCycleDetailsObj] = useState(initialState);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [goalMetrics, setGoalMetrics] = useState([]);
  const [cycleGoalDescription, setCycleGoalsDescription] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { cycleName, testCycle, startDate, endDate, threshold } = cycleDetailsObj;

  const handleChange = (value, key, type = 'text', item = '') => {
    let newObj = { ...cycleDetailsObj };
    if (type === 'numeric') {
      if (key === 'threshold') {
        if (/^\d{0,10}$/.test(Number(value))) {
          newObj[key] = value;
          setCycleDetailsObj({ ...newObj });
        }
      }
      if (/^\d{0,6}$/.test(Number(value))) {
        newObj[key] = value;
        setCycleDetailsObj({ ...newObj });
      }
    } else {
      if (item === 'Start Date') {
        newObj['endDate'] = '';
      }
      newObj[key] = type === 'toggle' ? (value ? 'Yes' : 'No') : value;
      setCycleDetailsObj({ ...newObj });
    }
  };

  const handleSave = async () => {
    if (screenName !== 'home') {
      handleRunCycle();
    } else {
      const getStructuredFormatData = (data) => {
        let filteredList = data?.reduce((acc, obj) => {
          const key = Object.keys(obj).find((key) => key !== 'id');
          acc[convertStringToCamelCase(key)] = Number(obj[key]);
          return acc;
        }, {});
        return filteredList;
      };
      let filteredGoalMetricsData = await getStructuredFormatData(goalMetrics);
      let payload = {
        cycleName: cycleName,
        businessUnitName: selectedBu,
        startYearPeriod: startDate,
        endYearPeriod: endDate,
        testCycle: testCycle === 'Yes' ? true : false,
        cycleTarget: cycleGoalDescription
      };
      if (cycleName === '') {
        triggerNotification('cycle name cannot be blank', 'info');
      } else if (startDate === null || startDate === '') {
        triggerNotification('Start Date cannot be blank', 'info');
      } else if (endDate === null || endDate === '') {
        triggerNotification('End Date cannot be blank', 'info');
      } else {
        handleCreateCycle({ ...payload, ...filteredGoalMetricsData });
        handleClose();
        setGoalMetrics([]);
        setCycleGoalsDescription('');
        setCycleDetailsObj(initialState);
      }
    }
  };

  const handleCancel = () => {
    handleClose();
    setGoalMetrics([]);
    setCycleGoalsDescription('');
    setCycleDetailsObj(initialState);
  };

  useEffect(() => {
    if (Object.keys(selectedCycleObj)?.length > 0) {
      setCycleDetailsObj({
        cycleName: selectedCycleObj?.cycleName,
        testCycle: selectedCycleObj?.testCycle
          ? selectedCycleObj?.testCycle
            ? 'Yes'
            : 'No'
          : 'No',
        activeCycle: selectedCycleObj?.activeStatus
          ? selectedCycleObj?.activeStatus
            ? 'Yes'
            : 'No'
          : 'No',
        startDate: selectedCycleObj?.startYearPeriod,
        endDate: selectedCycleObj?.endYearPeriod,
        threshold: selectedCycleObj?.threshold
      });
      let filteredMetrics = [];
      goalMetricOptions
        ?.map((item) => item?.displayText)
        ?.map((item, index) => {
          filteredMetrics.push({
            id: index,
            [item]: selectedCycleObj[convertStringToCamelCase(item)]
          });
          return item;
        });
      setGoalMetrics(filteredMetrics);
      setCycleGoalsDescription(selectedCycleObj?.cycleTarget);
    }
  }, []);

  return (
    <>
      <Drawer
        anchor="right"
        open={showDrawer}
        onClose={() => handleClose()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 500 },
            marginTop: '50px',
            height: 'calc(100% - 50px)',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px 20px 0px 20px'
          }
        }}>
        <div className="cycle-header-container">
          <div className="cycle-header-text">Set up Cycle</div>
          <CloseIcon className="cycle-header-close-icon" onClick={() => handleClose()} />
        </div>
        <div className="cycle-details-container">
          <h3 className="cycle-details-title">Cycle Details</h3>
          <div className="cycle-details-input-section-1">
            <Tooltip
              title={
                <div className="cycle-name-tooltip" style={{ padding: '0px' }}>
                  {cycleName}
                </div>
              }
              placement="top">
              <TextInput
                data-testid="cycle-name-input"
                className="cycle-name-input"
                disabled={screenName !== 'home'}
                helperText=""
                value={cycleName}
                label="Cycle Name"
                onChange={(event) => handleChange(event.target.value, 'cycleName')}
                placeholderText=""
                showLabelIcon={false}
              />
            </Tooltip>
            {cycleToggleOptions?.map((item, index) => (
              <>
                <Toggle
                  key={item}
                  className="active-cycle-toggle"
                  disabled={screenName !== 'home'}
                  label={item}
                  checked={cycleDetailsObj[convertStringToCamelCase(item)] === 'Yes'}
                  onUpdate={(event) =>
                    handleChange(event, convertStringToCamelCase(item), 'toggle')
                  }
                  size="medium"
                  value={cycleDetailsObj[convertStringToCamelCase(item)]}
                  variant="horizontal"
                />
                {index === 0 && (
                  <Tooltip
                    title={
                      <div className="goal-result-info-container">
                        Test Cycle cannot be selected for Voting{' '}
                      </div>
                    }
                    placement="left-end"
                    style={{ padding: '0px' }}>
                    <InfoOutlinedIcon className="test-cycle-info" />
                  </Tooltip>
                )}
              </>
            ))}
          </div>
          <div className="cycle-details-input-section">
            {cycleDatesInputOptions?.map((item) => (
              <>
                <div className="dates-section">
                  <label className="date-labels">{item}</label>
                  {item !== 'Threshold' ? (
                    <Select
                      data-testid="date-selector"
                      disabled={screenName !== 'home'}
                      className="year-periods"
                      value={cycleDetailsObj[convertStringToCamelCase(item)]}
                      onChange={(event) =>
                        handleChange(
                          event.target.value,
                          convertStringToCamelCase(item),
                          'text',
                          item
                        )
                      }>
                      {periodsData?.map((period) => (
                        <MenuItem
                          key={period}
                          value={period}
                          disabled={
                            item === 'End Date' &&
                            Number(cycleDetailsObj['startDate']) >= Number(period)
                          }>
                          {period}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <span/>
                  )}
                </div>
              </>
            ))}
          </div>
          <Divider className="cycle-section-divider" />
          <div className="goals-info-container">
            <div className="goals-info">
              <span className="goals-text">Goals</span>
              <Tooltip
                title={
                  <div className="goal-result-info-container">
                    These goals are only a guideline and will not impact the results of the
                    algorithm
                  </div>
                }
                placement="right-end"
                style={{ padding: '0px' }}>
                <InfoOutlinedIcon className="goal-icon-info" />
              </Tooltip>
            </div>
            {screenName === 'home' && (
              <div
                className="add-goals"
                data-testid="add-goals"
                onClick={() => setShowGoalsModal(true)}>
                {`${goalMetrics?.length === 0 ? 'Add' : 'Edit'} Goals`}
              </div>
            )}
          </div>
          <h3 className="cycle-target-text">CYCLE TARGET</h3>
          <div className="cycle-goal-descption">{cycleGoalDescription}</div>
          <table className="goal-metrics-table">
            <tr>
              <th>KEY METRICS</th>
              <th>TARGETS</th>
            </tr>
            {goalMetrics?.map((item) => (
              <tr key={item}>
                {Object.keys(item)?.length > 1 && (
                  <>
                    <td>{Object.keys(item)?.[1]}</td>
                    <td>{`${item[Object.keys(item)?.[1]]}%`}</td>
                  </>
                )}
              </tr>
            ))}
          </table>
        </div>
        <GoalsModal
          showModal={showGoalsModal}
          handleClose={() => setShowGoalsModal(false)}
          saveGoalInputs={(data) => setGoalMetrics(data)}
          setCycleGoalsDescription={(val) => setCycleGoalsDescription(val)}
          cycleGoalDescription={cycleGoalDescription}
          goalInputs={goalMetrics}
        />
        <div className="submit-container">
          <Button
            variant="secondary"
            data-testid="cycle-modal-close-btn"
            onClick={() => handleCancel()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="create-cycle"
            onClick={() => handleSave()}
            disabled={
              (screenName !== 'home' && !selectedCycleObj?.testCycle && isSteeringRecoCompleted) ||
              (!selectedCycleObj?.testCycle &&
                isSteeringRecoCompleted &&
                selectedCycleObj?.activeStatus)
            }>
            {screenName === 'home' ? 'Save' : 'Run Cycle'}
          </Button>
        </div>
      </Drawer>
      <ConfirmationModal
        open={showConfirmationModal}
        title={'New cycle cannot be created'}
        text={`Voting is still in-progress so new cycle cannot be created However, you can create a test cycle. Do you want to proceed?`}
        cancelText="Cancel"
        SubmitText={'Proceed'}
        onClose={() => {
          setShowConfirmationModal(false);
          handleClose();
          setCycleDetailsObj(initialState);
        }}
        onSubmit={() => {
          setShowConfirmationModal(false);
        }}
        textAlign="start"
      />
    </>
  );
};

export default CycleDrawer;
